/**
 * Identity-vault cryptography (browser-side only).
 *
 * Design: docs/compliance/PSEUDONYMOUS_IDENTITY_PILOT.md
 *
 * Envelope model (textbook KEK/DEK — no invented cryptography):
 *   patient records  <- AES-256-GCM under -> master key (random, WebCrypto)
 *   master key       <- RSA-OAEP wrapped to each user's public key
 *   private key      <- AES-GCM sealed under a passphrase-derived key
 *   passphrase       -> PBKDF2-SHA256 (600k iterations, per-seal random salt)
 *
 * NOTHING in this file ever sends key material anywhere: callers upload only
 * the sealed/wrapped envelopes these functions return. Raw keys live in
 * memory (non-extractable CryptoKey where possible) and vanish with the tab.
 */

// OWASP-recommended PBKDF2-SHA256 work factor. (Argon2 is the design-doc
// ideal; PBKDF2 is the native-WebCrypto interim — no third-party WASM dep.)
const PBKDF2_ITERATIONS = 600_000;

const subtle = window.crypto.subtle;

// ---------------------------------------------------------------------------
// Small codecs
// ---------------------------------------------------------------------------

const te = new TextEncoder();
const td = new TextDecoder();

export function bytesToB64(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = '';
  arr.forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return window.btoa(bin);
}

export function b64ToBytes(b64: string): Uint8Array {
  const bin = window.atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return arr;
}

function randomBytes(n: number): Uint8Array {
  const arr = new Uint8Array(n);
  window.crypto.getRandomValues(arr);
  return arr;
}

// ---------------------------------------------------------------------------
// Passphrase sealing (PBKDF2 -> AES-GCM)
// ---------------------------------------------------------------------------

/** JSON envelope for anything sealed under a passphrase/recovery code. */
export interface SealedEnvelope {
  v: 1;
  kdf: 'PBKDF2-SHA256';
  iterations: number;
  salt: string; // b64
  iv: string; // b64
  ciphertext: string; // b64
}

async function deriveKek(passphrase: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const baseKey = await subtle.importKey('raw', te.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function sealWithPassphrase(data: Uint8Array, passphrase: string): Promise<string> {
  const salt = randomBytes(16);
  const iv = randomBytes(12);
  const kek = await deriveKek(passphrase, salt, PBKDF2_ITERATIONS);
  const ciphertext = await subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, kek, data as BufferSource);
  const envelope: SealedEnvelope = {
    v: 1,
    kdf: 'PBKDF2-SHA256',
    iterations: PBKDF2_ITERATIONS,
    salt: bytesToB64(salt),
    iv: bytesToB64(iv),
    ciphertext: bytesToB64(ciphertext),
  };
  return JSON.stringify(envelope);
}

/** Throws (DOMException OperationError) on a wrong passphrase. */
export async function unsealWithPassphrase(envelopeJson: string, passphrase: string): Promise<Uint8Array> {
  const envelope = JSON.parse(envelopeJson) as SealedEnvelope;
  // Reject a server-tampered envelope that tries to downgrade the KDF (a
  // malicious backend could serve iterations:1 or swap the algorithm to weaken
  // offline resistance on the legitimate user's machine). Pin the algorithm and
  // enforce a work-factor floor.
  if (envelope.kdf !== 'PBKDF2-SHA256' || envelope.v !== 1) {
    throw new Error('vault-envelope-unsupported');
  }
  if (!Number.isInteger(envelope.iterations) || envelope.iterations < PBKDF2_ITERATIONS) {
    throw new Error('vault-envelope-weak-kdf');
  }
  const kek = await deriveKek(passphrase, b64ToBytes(envelope.salt), envelope.iterations);
  const plain = await subtle.decrypt(
    { name: 'AES-GCM', iv: b64ToBytes(envelope.iv) as BufferSource },
    kek,
    b64ToBytes(envelope.ciphertext) as BufferSource,
  );
  return new Uint8Array(plain);
}

// ---------------------------------------------------------------------------
// Keypair (RSA-OAEP) — the "open padlock" enrollment model
// ---------------------------------------------------------------------------

const RSA_PARAMS: RsaHashedKeyGenParams = {
  name: 'RSA-OAEP',
  modulusLength: 4096,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: 'SHA-256',
};

export interface GeneratedKeypair {
  publicKeySpkiB64: string;
  privateKeyPkcs8: Uint8Array;
}

export async function generateKeypair(): Promise<GeneratedKeypair> {
  const pair = await subtle.generateKey(RSA_PARAMS, true, ['encrypt', 'decrypt']);
  const spki = await subtle.exportKey('spki', pair.publicKey);
  const pkcs8 = await subtle.exportKey('pkcs8', pair.privateKey);
  return { publicKeySpkiB64: bytesToB64(spki), privateKeyPkcs8: new Uint8Array(pkcs8) };
}

export async function importPrivateKey(pkcs8: Uint8Array): Promise<CryptoKey> {
  return subtle.importKey('pkcs8', pkcs8 as BufferSource, { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['decrypt']);
}

export async function importPublicKey(spkiB64: string): Promise<CryptoKey> {
  return subtle.importKey('spki', b64ToBytes(spkiB64) as BufferSource, { name: 'RSA-OAEP', hash: 'SHA-256' }, false, [
    'encrypt',
  ]);
}

// ---------------------------------------------------------------------------
// Master key (AES-256-GCM)
// ---------------------------------------------------------------------------

export function generateMasterKeyBytes(): Uint8Array {
  return randomBytes(32);
}

export async function importMasterKey(raw: Uint8Array): Promise<CryptoKey> {
  return subtle.importKey('raw', raw as BufferSource, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/** A non-secret fingerprint of the master key, for trust-on-first-use pinning.
 *
 * SHA-256 of a domain-separated copy of the raw key — a one-way digest, so it
 * reveals nothing about the key, but changes completely if the key changes.
 * Pinned locally on first unlock and compared on every unlock to detect a
 * server-substituted master key (the zero-knowledge trust ceiling). */
export async function masterKeyFingerprint(masterRaw: Uint8Array): Promise<string> {
  const domain = te.encode('aimepic-identity-vault:mk-fingerprint:v1');
  const buf = new Uint8Array(domain.length + masterRaw.length);
  buf.set(domain, 0);
  buf.set(masterRaw, domain.length);
  const digest = await subtle.digest('SHA-256', buf as BufferSource);
  return bytesToB64(digest);
}

/** Snap the master key inside a public-key padlock (grant / self-grant). */
export async function wrapMasterKeyForPublicKey(masterRaw: Uint8Array, publicKeySpkiB64: string): Promise<string> {
  const publicKey = await importPublicKey(publicKeySpkiB64);
  const wrapped = await subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, masterRaw as BufferSource);
  return bytesToB64(wrapped);
}

export async function unwrapMasterKeyWithPrivateKey(wrappedB64: string, privateKey: CryptoKey): Promise<Uint8Array> {
  const raw = await subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, b64ToBytes(wrappedB64) as BufferSource);
  return new Uint8Array(raw);
}

// ---------------------------------------------------------------------------
// Record encryption (identity records under the master key)
// ---------------------------------------------------------------------------

interface RecordEnvelope {
  v: 1;
  iv: string; // b64
  ciphertext: string; // b64
}

export async function encryptRecord(masterKey: CryptoKey, record: unknown): Promise<string> {
  const iv = randomBytes(12);
  const ciphertext = await subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    masterKey,
    te.encode(JSON.stringify(record)) as BufferSource,
  );
  const envelope: RecordEnvelope = { v: 1, iv: bytesToB64(iv), ciphertext: bytesToB64(ciphertext) };
  return JSON.stringify(envelope);
}

export async function decryptRecord<T>(masterKey: CryptoKey, envelopeJson: string): Promise<T> {
  const envelope = JSON.parse(envelopeJson) as RecordEnvelope;
  const plain = await subtle.decrypt(
    { name: 'AES-GCM', iv: b64ToBytes(envelope.iv) as BufferSource },
    masterKey,
    b64ToBytes(envelope.ciphertext) as BufferSource,
  );
  return JSON.parse(td.decode(plain)) as T;
}

// ---------------------------------------------------------------------------
// Identifier generation
// ---------------------------------------------------------------------------

// No 0/O/1/I — pseudonyms get read aloud and typed.
const PSEUDONYM_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function randomFromAlphabet(length: number): string {
  const bytes = randomBytes(length);
  let out = '';
  for (let i = 0; i < length; i += 1) out += PSEUDONYM_ALPHABET[bytes[i] % PSEUDONYM_ALPHABET.length];
  return out;
}

/** e.g. PSN-7Q3F2K8M — matches the backend's PSEUDONYM_RE. */
export function generatePseudonym(): string {
  return `PSN-${randomFromAlphabet(8)}`;
}

/** Printed once, stored nowhere: e.g. F3KQ7-2MWPX-9RHT4-JC6VD-5YBNL */
export function generateRecoveryCode(): string {
  return Array.from({ length: 5 }, () => randomFromAlphabet(5)).join('-');
}

export function isPseudonym(value: string): boolean {
  return /^PSN-[A-Z0-9]{4,16}$/.test((value || '').trim());
}
